import { LoadingButton } from "@mui/lab";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { X } from "react-feather";
import { CreateEmployerFundTopUpResponse } from "../../models/funding/createEmployerFundTopUpResponse";
import { EmployerFundTopUpStatus } from "../../models/funding/employerFundTopUpStatus";
import {
  EmployerPaymentMethod,
  EmployerPaymentMethodType
} from "../../models/funding/employerPaymentMethod";
import { useData } from "../../utils/useData";
import { handlePaymentMethodDisplayString } from "./PaymentMethodPreview";
import { useTopUpEmployerFund } from "../../hooks/employerFunds/useTopUpEmployerFund";

const purchaseOrderPaymentMethod: EmployerPaymentMethod = {
  id: "purchase-order",
  type: EmployerPaymentMethodType.PURCHASE_ORDER,
  employerId: "",
  isDefault: false,
  createdAt: new Date().toISOString()
};

export const AddFundsModal: FC<{
  paymentMethods: EmployerPaymentMethod[];
  open: boolean;
  handleClose: () => void;
}> = ({ paymentMethods, open, handleClose }) => {
  const { employer } = useData();

  const paymentMethodOptions = [...paymentMethods, purchaseOrderPaymentMethod];

  const [amount, setAmount] = useState<number | undefined>();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    EmployerPaymentMethod | undefined
  >();
  const [reference, setReference] = useState<string>(
    employer.data?.invoicingOptions.reference || ""
  );

  const [validationError, setValidationError] = useState<string | undefined>();
  const [apiError, setApiError] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | undefined>();

  const resetModalState = () => {
    setAmount(undefined);
    setValidationError(undefined);
    setApiError(undefined);
    setSuccessMessage(undefined);
    setReference(employer.data?.invoicingOptions.reference || "");
  };

  const calculateAmountWithSurcharge = (baseAmount: number): number => {
    if (!selectedPaymentMethod || !baseAmount) return baseAmount;

    if (
      selectedPaymentMethod.type === EmployerPaymentMethodType.CARD &&
      employer.data?.paymentOptions?.cardPaymentSurchargePercent
    ) {
      const surchargePercent =
        employer.data.paymentOptions.cardPaymentSurchargePercent;
      return baseAmount * (1 + surchargePercent);
    } else if (
      selectedPaymentMethod.type === EmployerPaymentMethodType.DIRECT_DEBIT &&
      employer.data?.paymentOptions?.directDebitPaymentSurchargePercent
    ) {
      const surchargePercent =
        employer.data.paymentOptions.directDebitPaymentSurchargePercent;
      return baseAmount * (1 + surchargePercent);
    }

    return baseAmount;
  };

  const getSurchargeAmount = (baseAmount: number): number => {
    if (!selectedPaymentMethod || !baseAmount) return 0;

    if (
      selectedPaymentMethod.type === EmployerPaymentMethodType.CARD &&
      employer.data?.paymentOptions?.cardPaymentSurchargePercent
    ) {
      const surchargePercent =
        employer.data.paymentOptions.cardPaymentSurchargePercent;
      return baseAmount * surchargePercent;
    } else if (
      selectedPaymentMethod.type === EmployerPaymentMethodType.DIRECT_DEBIT &&
      employer.data?.paymentOptions?.directDebitPaymentSurchargePercent
    ) {
      const surchargePercent =
        employer.data.paymentOptions.directDebitPaymentSurchargePercent;
      return baseAmount * surchargePercent;
    }

    return 0;
  };



  const topUpEmployerFund = useTopUpEmployerFund({
    onSuccess: (data: CreateEmployerFundTopUpResponse) => {
      handleSuccessMessage(data.status, data.invoiceUrl);
      setApiError(undefined);
    },
    onError: (error: Error) => {
      setApiError(
        error.message
      );
    }
  });

  const handleSuccessMessage = (
    status: EmployerFundTopUpStatus,
    invoiceUrl?: string
  ) => {
    if (
      status === EmployerFundTopUpStatus.APPROVED &&
      selectedPaymentMethod?.type === EmployerPaymentMethodType.CARD
    ) {
      setSuccessMessage(
        "Your funds have been added successfully, it could take a few minutes for this to reflect in your fund."
      );
      setTimeout(() => {
        resetModalState();
        handleClose();
      }, 3000);
    }

    if (
      status === EmployerFundTopUpStatus.PENDING &&
      selectedPaymentMethod?.type === EmployerPaymentMethodType.DIRECT_DEBIT
    ) {
      setSuccessMessage(
        "Your funds are pending. Direct debit payments can take up to 2-3 business days to process."
      );
      setTimeout(() => {
        resetModalState();
        handleClose();
      }, 3000);
    }

    if (
      status === EmployerFundTopUpStatus.PENDING &&
      selectedPaymentMethod?.type === EmployerPaymentMethodType.PURCHASE_ORDER
    ) {
      setSuccessMessage(
        `Here is your <a href='${invoiceUrl}' style='color: inherit; text-decoration: underline; cursor: pointer;'>purchase order invoice</a>.<br/><br/>Payment of this invoice will automatically add funds into your Extraordinary account. You can view this invoice anytime by viewing your top ups under activity, and opening the purchase order link.`
      );
    }
  };

  const validateFields = () => {
    if (!selectedPaymentMethod) {
      setValidationError("Must select a payment method");
      return false;
    }

    if (
      selectedPaymentMethod.type === EmployerPaymentMethodType.PURCHASE_ORDER &&
      reference
    ) {
      if (reference.length > 50) {
        setValidationError("Reference must be 50 characters or less");
        return false;
      }
    }

    if (!amount) {
      setValidationError("Amount is required");
      return false;
    }

    if (amount < 0.5) {
      setValidationError("Amount must be more than $0.50");
      return false;
    }

    setValidationError(undefined);
    return true;
  };

  const handleSubmit = () => {
    if (!validateFields()) return;

    setApiError(undefined);
    const amountWithSurcharge = calculateAmountWithSurcharge(amount ?? 0);

    // Not sending paymentMethodId for purchase order as it's not a real stored payment method
    const employerPaymentMethodId =
      selectedPaymentMethod?.type === EmployerPaymentMethodType.PURCHASE_ORDER
        ? undefined
        : selectedPaymentMethod!.id;

    topUpEmployerFund.mutate({
      amountWithSurcharge: amountWithSurcharge,
      employerPaymentMethodId,
      employerPaymentMethodType: selectedPaymentMethod!.type,
      reference:
        selectedPaymentMethod?.type === EmployerPaymentMethodType.PURCHASE_ORDER
          ? reference
          : undefined
    });
  };

  // Set default payment method to be selected initially
  useEffect(() => {
    const defaultPm = paymentMethods.find((pm) => pm.isDefault);
    setSelectedPaymentMethod(defaultPm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethods]);

  useEffect(() => {
    setValidationError(undefined);
    setApiError(undefined);
  }, [selectedPaymentMethod]);

  const surchargeAmount = getSurchargeAmount(amount ?? 0);
  const amountWithSurcharge = calculateAmountWithSurcharge(amount ?? 0);

  const handleModalClose = () => {
    resetModalState();
    handleClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth={true}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <DialogTitle>Add Funds</DialogTitle>
        <IconButton onClick={handleModalClose} sx={{ mr: 2 }}>
          <X />
        </IconButton>
      </Box>
      <DialogContent>
        {successMessage ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <Typography variant="h6">Success!</Typography>
            <Typography
              dangerouslySetInnerHTML={{ __html: successMessage || "" }}
            />
          </Box>
        ) : (
          <>
            <Box>
              <Typography my={1}>Payment Methods</Typography>
              <Select
                value={selectedPaymentMethod?.id || ""}
                onChange={(e) => {
                  const selectedMethod = paymentMethodOptions.find(
                    (method) => method.id === e.target.value
                  );
                  setSelectedPaymentMethod(selectedMethod);
                }}
                size="small"
                fullWidth
              >
                {paymentMethodOptions.map((paymentMethod) => (
                  <MenuItem key={paymentMethod.id} value={paymentMethod.id}>
                    {handlePaymentMethodDisplayString(paymentMethod)}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box my={2}>
              {selectedPaymentMethod?.type ===
                EmployerPaymentMethodType.PURCHASE_ORDER && (
                <Box my={2}>
                  <Typography fontSize={16} mt={1} mb={2} color="grey">
                    This will generate an invoice to be paid by bank transfer
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    value={reference}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 50 && /^[a-zA-Z0-9-]*$/.test(value)) {
                        setReference(value);
                      }
                    }}
                    placeholder="Invoice reference e.g. Purchase Order Number"
                  />
                </Box>
              )}

              <Typography>Amount</Typography>
              <TextField
                variant="standard"
                fullWidth
                value={amount}
                onChange={(e) => {
                  const parsedValue = Number(e.currentTarget.value);
                  if (Number.isNaN(parsedValue)) return;
                  setAmount(+e.currentTarget.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  )
                }}
              />

              <Box display="flex" mt={1} mb={3}>
                {amount && amount > 0 ? (
                  <>
                    {selectedPaymentMethod?.type ===
                      EmployerPaymentMethodType.CARD &&
                      employer.data?.paymentOptions
                        ?.cardPaymentSurchargePercent && (
                        <Typography variant="body2" color="text.secondary">
                          Card surcharge (
                          {employer.data.paymentOptions
                            .cardPaymentSurchargePercent * 100}
                          %) ${surchargeAmount.toFixed(2)}
                        </Typography>
                      )}
                    {selectedPaymentMethod?.type ===
                      EmployerPaymentMethodType.DIRECT_DEBIT &&
                      employer.data?.paymentOptions
                        ?.directDebitPaymentSurchargePercent && (
                        <Typography variant="body2" color="text.secondary">
                          Direct Debit surcharge (
                          {employer.data.paymentOptions
                            .directDebitPaymentSurchargePercent * 100}
                          %): ${surchargeAmount.toFixed(2)}
                        </Typography>
                      )}
                    {amount > 0 && (
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        marginLeft="auto"
                      >
                        Total ${amountWithSurcharge.toFixed(2)}
                      </Typography>
                    )}
                  </>
                ) : null}
              </Box>
            </Box>

            {(validationError || apiError) && (
              <Typography color={"error"} my={2}>
                {validationError || apiError}
              </Typography>
            )}

            <LoadingButton
              variant="contained"
              fullWidth
              loading={topUpEmployerFund.isLoading}
              onClick={() => handleSubmit()}
            >
              Confirm
            </LoadingButton>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
